import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import Modal, { Field, TextInput, TextArea, Select, PrimaryButton, SecondaryButton } from "@/components/FormKit";
import { toast } from "sonner";
import { fmtDateTime, relativeTime } from "@/lib/format";

const initial = { subject: "", body: "", priority: "normal" };

export default function Tickets() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [reply, setReply] = useState("");

  const load = async () => setItems((await api.get("/customer/tickets")).data);
  useEffect(() => { load(); }, []);

  const openTicket = async (id) => {
    const { data } = await api.get(`/customer/tickets/${id}`);
    setActive(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customer/tickets", form);
      toast.success("Ticket raised");
      setOpen(false);
      setForm(initial);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    try {
      await api.post(`/customer/tickets/${active.id}/responses`, { body: reply });
      setReply("");
      openTicket(active.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="customer-tickets-page">
      <PageHeader
        eyebrow="Support"
        title="Support tickets"
        description="Reach out to your developer with questions or issues."
        actions={<PrimaryButton onClick={() => setOpen(true)} testId="new-ticket-button">+ Raise Ticket</PrimaryButton>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="grid-surface divide-y divide-stone-100 max-h-[70vh] overflow-y-auto">
          {items.length === 0 && <div className="p-8 text-center overline text-stone-400">No tickets yet</div>}
          {items.map((t) => (
            <button
              key={t.id}
              onClick={() => openTicket(t.id)}
              data-testid={`ticket-item-${t.id}`}
              className={`w-full text-left p-4 hover:bg-stone-50 border-l-4 transition-colors ${
                active?.id === t.id ? "border-l-brand-500 bg-stone-50" : "border-l-transparent"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-display font-bold text-sm flex-1">{t.subject}</div>
                <StatusBadge value={t.status} />
              </div>
              <div className="text-xs text-stone-500 mt-1 font-mono">{relativeTime(t.created_at)}</div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {active ? (
            <div className="grid-surface p-6" data-testid="active-ticket">
              <div className="border-b border-stone-200 pb-4">
                <div className="overline text-stone-500">{fmtDateTime(active.created_at)}</div>
                <h3 className="font-display text-2xl font-bold mt-1">{active.subject}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <StatusBadge value={active.status} />
                  <StatusBadge value={active.priority} />
                </div>
                <p className="text-stone-700 mt-3">{active.body}</p>
              </div>

              <div className="mt-4 space-y-3">
                {(active.responses || []).map((r) => (
                  <div key={r.id} className={`p-3 border ${r.author_role === "company" ? "border-brand-500 bg-brand-50" : "border-stone-200 bg-white"}`}>
                    <div className="text-xs uppercase tracking-wider font-bold text-stone-500">
                      {r.author_role === "company" ? "BUILDER" : "YOU"} · {r.author_name}
                    </div>
                    <div className="text-sm text-stone-800 mt-1">{r.body}</div>
                    <div className="text-[10px] font-mono text-stone-400 mt-1">{fmtDateTime(r.created_at)}</div>
                  </div>
                ))}
              </div>

              <form onSubmit={sendReply} className="mt-4 border-t border-stone-200 pt-4">
                <TextArea testId="customer-reply-input" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Add a message…" />
                <div className="flex justify-end mt-2">
                  <PrimaryButton testId="send-customer-reply" type="submit">Send</PrimaryButton>
                </div>
              </form>
            </div>
          ) : (
            <div className="grid-surface border-dashed p-10 text-center overline text-stone-400">
              Select a ticket to view
            </div>
          )}
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Raise a support ticket">
        <form onSubmit={submit}>
          <Field label="Subject"><TextInput testId="form-ticket-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></Field>
          <Field label="Describe your issue"><TextArea testId="form-ticket-body" rows={4} required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></Field>
          <Field label="Priority">
            <Select testId="form-ticket-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </Select>
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-ticket">Submit</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
